import { setWorkspaceSessionUser } from '../db/session-user-db-task'
import type { createWriteGate } from '../db/page-commit-buffer'
import { pageParentColumn } from '../db/page-read'
import { normalizeLexiconTrigramText } from './lexicon-trigram'
import { ruleTitleFromKey } from './rule-title'
import type { Tel } from '@w/tel'
import { writeWorkspace } from '../db/workspace-write'
import { blockId, type Blocks } from '@w/content-blocks'
import { commitPageEdit, movePage } from '../db/commit-page-edit'
import { crdt, type Json } from '@w/crdt'
import { pageDocument, writeDocument } from '@w/content-blocks-editor/doc-bindings-for-page'
import { s } from '@w/schema'
import { newPageDocument, pageStructure, type PageDocumentContent } from '../db/page-document'
import { pageMetaColumns, pageMetaSchema, pageReadColumns, pageReadSchema } from '../db/page-read'
import { ulid } from '@w/utils'
import type { FlashcardsDb } from '../router/flashcards-fe.router-ctx'
import type { PageContent } from '../db/schema'
import { mintQuestionBlocks } from './entry-blocks'
import { mintedCards } from './lexicon-cards'
import {
  adoptEntryContent,
  assertValidEntry,
  type CachedEntry,
  type CachedRule,
  type TranslationLanguage,
  type Language,
} from './lexicon-model'

import type { CliPageCommands } from '../cli/flashcards-repo-sync'

export type AdoptDeps = {
  db: FlashcardsDb
  writes: ReturnType<typeof createWriteGate>
  pageCommands: CliPageCommands
  tel: Tel
  afterCommit?: (workspaceId: string) => void | Promise<void>
  afterLocalCommit?: (spaceId: string) => void | Promise<void>
  userId: string
  spaceId: string
  deviceId: string
  now: () => Date
}

// Serialize language creation and adoption within this workspace session.
const languageWrites = new WeakMap<FlashcardsDb, Map<string, Promise<unknown>>>()
function _withLanguageWrite<T>(deps: AdoptDeps, language: Language, write: () => Promise<T>): Promise<T> {
  let writes = languageWrites.get(deps.db)
  if (!writes) { writes = new Map(); languageWrites.set(deps.db, writes) }
  const key = `${deps.spaceId}:${language}`
  const pending = writes.get(key) ?? Promise.resolve()
  const result = pending.then(write, write)
  writes.set(key, result)
  const release = () => { if (writes.get(key) === result) writes.delete(key) }
  void result.then(release, release)
  return result
}

function _admitAdoption<T>(deps: AdoptDeps, operation: (scoped: AdoptDeps) => Promise<T>): Promise<T> {
  return writeWorkspace({ writes: deps.writes, afterCommit: workspaceId => deps.afterCommit?.(workspaceId), afterLocalCommit: deps.afterLocalCommit }, deps.spaceId, async () => {
    await setWorkspaceSessionUser(deps.db.driver, deps.userId)
    return deps.db.transaction(tx => operation({ ...deps, db: tx,
      writes: { ...deps.writes, run: work => Promise.resolve().then(work) },
      afterCommit: undefined, afterLocalCommit: undefined,
    }))
  })
}

export function adoptEntry(deps: AdoptDeps, args: Parameters<typeof _adoptEntry>[1]) {
  return _withLanguageWrite(deps, args.entry.language, () => _admitAdoption(deps, scoped => _adoptEntry(scoped, args)))
}

export function ensureLanguagePage(deps: AdoptDeps, language: Language) {
  return _withLanguageWrite(deps, language, () => _admitAdoption(deps, scoped => _ensureLanguagePage(scoped, language)))
}

async function _adoptEntry(
  deps: AdoptDeps,
  args: {
    entry: CachedEntry
    rule?: CachedRule
    selectedSenseIds?: string[]
    l1: TranslationLanguage
  },
): Promise<{ pageId: string; rulePageId?: string; created: boolean }> {
  const { pageId: languagePageId, vocabularyPageId, grammarPageId } = await _ensureLanguagePage(deps, args.entry.language,
    args.rule?.language === args.entry.language ? args.rule : undefined)
  const existing = await deps.db.query`
    SELECT ${deps.db.pages.id} AS pageId
    FROM ${deps.db.pages}
    WHERE ${deps.db.pages.entryId.eq(args.entry.id)}
      AND ${deps.db.pages.kind.eq('lexiconEntry')} AND ${deps.db.pages.deletedAt.isNull()}
    ORDER BY ${deps.db.pages.id}
    LIMIT 1
  `.executeOne({ pageId: deps.db.pages.id })
  if (existing) return { pageId: existing.pageId, created: false }

  const adopted = adoptEntryContent(args.entry, {
    selectedSenseIds: args.selectedSenseIds,
    mintId: ulid,
  })
  Object.assign(adopted, { headwordKey: normalizeLexiconTrigramText(adopted.headword) })
  assertValidEntry(adopted)

  const matchingRule =
    args.entry.kind === 'lexeme' &&
    args.rule?.key === args.entry.inflectionClass &&
    args.rule.language === args.entry.language
      ? args.rule
      : undefined
  const questionBlocks = mintedCards(adopted, matchingRule?.block).flatMap(card =>
    mintQuestionBlocks(adopted, card.qid, card.kind, args.l1))

  const pageId = ulid()
  // Entries hang off the language's vocabulary page, not the language page
  // itself: nav STOPS at vocabulary, so thousands of entries never enter a
  // tree walk while ordinary notes under the language keep walking.
  await _createPage(deps, {
    pageId,
    title: adopted.headword,
    parentId: vocabularyPageId,
    content: { kind: 'lexiconEntry', entry: adopted },
    questionBlocks,
  })

  let rulePageId: string | undefined
  if (args.entry.kind === 'lexeme' && matchingRule) {
    rulePageId = await _ensureRulePage(
      deps,
      languagePageId,
      grammarPageId,
      matchingRule,
    )
  }

  return { pageId, ...(rulePageId ? { rulePageId } : {}), created: true }
}

async function _ensureLanguagePage(
  deps: AdoptDeps,
  language: Language,
  cachedRule?: CachedRule,
): Promise<{ pageId: string; vocabularyPageId: string; grammarPageId: string; created: boolean }> {
  // Read/validate one matched candidate; unrelated documents are never decoded (req:42).
  const existing = await deps.db.query`
    SELECT ${pageReadColumns(deps.db.pages)}
    FROM ${deps.db.pages}
    WHERE ${deps.db.pages.id} IN (
      SELECT ${deps.db.pages.id} FROM ${deps.db.pages}
      WHERE ${deps.db.pages.kind.eq('language')}
        AND ${deps.db.pages.language.eq(language)}
        AND ${deps.db.pages.deletedAt.isNull()}
      ORDER BY ${deps.db.pages.id} LIMIT 1
    )
  `.execute(pageReadSchema(deps.spaceId))
  const page = existing[0]
  if (page) {
    return {
      pageId: page.id,
      vocabularyPageId: await ensureVocabularyPage(deps, page.id),
      grammarPageId: await _ensureGrammarPage(deps, page.id, cachedRule),
      created: false,
    }
  }

  const pageId = ulid()
  await _createPage(deps, {
    pageId,
    title: _languageTitle(language),
    // A language page sits under the caller's personal root (its directory entry, via the page commands).
    parentId: deps.pageCommands.personalRootId,
    content: {
      kind: 'language',
      language,
      children: [{ id: blockId(), type: 'p', children: [] }],
    },
  })
  return {
    pageId,
    vocabularyPageId: await ensureVocabularyPage(deps, pageId),
    grammarPageId: await _ensureGrammarPage(deps, pageId),
    created: true,
  }
}

/**
 * The language page's system subpage. A marker, not a container: it stores
 * nothing, and every entry is its own row pointing at it (Core 3).
 */
export async function ensureVocabularyPage(
  deps: AdoptDeps,
  languagePageId: string,
): Promise<string> {
  const existing = await deps.db.query`
    SELECT ${deps.db.pages.id}
    FROM ${deps.db.pages}
    WHERE ${pageParentColumn(deps.db.pages)} = ${languagePageId}
      AND ${deps.db.pages.id} IN (SELECT id FROM pages WHERE ${deps.db.pages.kind.eq('vocabulary')})
      AND ${deps.db.pages.deletedAt.isNull()}
    ORDER BY ${deps.db.pages.id}
    LIMIT 1
  `.executeOne({ id: deps.db.pages.id })
  if (existing) return existing.id

  const pageId = ulid()
  await _createPage(deps, {
    pageId,
    title: 'Vocabulary',
    readonly: true,
    parentId: languagePageId,
    content: { kind: 'vocabulary' },
  })
  return pageId
}

async function _ensureGrammarPage(deps: AdoptDeps, languagePageId: string, cachedRule?: CachedRule): Promise<string> {
  // Legacy discovery uses indexed attributes, never a decoded document scan.
  const pages = await deps.db.query`
    SELECT ${deps.db.pages.id}, ${deps.db.pages.title}, ${deps.db.pages.kind},
      ${deps.db.pages.translatable}, ${pageParentColumn(deps.db.pages)} AS parentId,
      (SELECT json_extract(parts.fields,'$.key') FROM crdt_attr_lookup('idx_pages_crdt_attr',NULL,${deps.db.pages}.rowid) parts
        WHERE json_type(parts.fields,'$.qid')='text' AND json_extract(parts.fields,'$.type')='rule'
        ORDER BY parts.docOrder LIMIT 1) AS ruleKey
    FROM ${deps.db.pages}
    WHERE ${pageParentColumn(deps.db.pages)} = ${languagePageId}
      AND ${deps.db.pages.kind.inArray(['note', 'grammar'])}
      AND ${deps.db.pages.deletedAt.isNull()}
    ORDER BY ${deps.db.pages.id}
  `.execute(s.object({ id: deps.db.pages.id.zodType, title: deps.db.pages.title.zodType,
    kind: deps.db.pages.kind.zodType, translatable: deps.db.pages.translatable.zodType,
    parentId: s.string().nullable(), ruleKey: s.string().nullable() }))
  const legacy = pages.filter(page => page.kind === 'note' && page.translatable && page.ruleKey === null)
  // Grammar is an ordinary note page; a page with the retired `grammar` marker kind becomes one. A settled Grammar is
  // found by title with no further read; otherwise the note holding rule pages is Grammar, so a renamed one stays.
  const marker = pages.find(page => page.kind === 'grammar')
  const titled = marker ? undefined : legacy.find(page => page.title === 'Grammar')
  const others = legacy.filter(page => page !== titled)
  const nested = others.length > 0 ? await deps.db.query`
    SELECT ${pageMetaColumns(deps.db.pages)}, json_extract(parts.fields,'$.key') AS ruleKey
    FROM ${deps.db.pages} CROSS JOIN crdt_attr_lookup('idx_pages_crdt_attr',NULL,${deps.db.pages}.rowid) parts
    WHERE ${pageParentColumn(deps.db.pages)} IN (SELECT value FROM json_each(${JSON.stringify(others.map(page => page.id))}))
      AND ${deps.db.pages.kind.eq('note')} AND ${deps.db.pages.deletedAt.isNull()}
      AND json_type(parts.fields,'$.qid')='text' AND json_extract(parts.fields,'$.type')='rule'
    ORDER BY ${deps.db.pages.id},parts.docOrder
  `.execute(s.object({ id: deps.db.pages.id.zodType, title: deps.db.pages.title.zodType,
    translatable: deps.db.pages.translatable.zodType, parentId: s.string().nullable(), ruleKey: s.string() })) : []
  const hub = marker ?? titled ?? others.find(page => nested.some(rule => rule.parentId === page.id))
  const grammarPageId = hub?.id ?? ulid()
  if (marker) {
    const row = await deps.db.query`SELECT ${pageReadColumns(deps.db.pages)} FROM ${deps.db.pages} WHERE ${deps.db.pages.id.eq(marker.id)}`
      .executeOneOrThrow(pageReadSchema(deps.spaceId))
    const doc = crdt.load(row.data, { user: deps.userId })
    const version = doc.version()
    doc.edit(tx => { tx.set('', 'content', pageStructure({ content: { kind: 'note', children: [] } }).content as Json) })
    await commitPageEdit(deps.db, { spaceId: deps.spaceId, pageId: grammarPageId,
      fields: { content: { kind: 'note', children: [] } }, userId: deps.userId, now: deps.now(),
      tel: deps.tel, frame: doc.encodeUpdate(version).bytes })
  } else if (!hub) {
    await _createPage(deps, { pageId: grammarPageId, title: 'Grammar', translatable: true,
      parentId: languagePageId, content: { kind: 'note', children: [] } })
  }

  const repaired = new Set<string>()
  const empty = crdt.create({})
  const frame = empty.encodeUpdate(empty.version()).bytes
  for (const page of [...pages, ...nested]) {
    if (!page.ruleKey || repaired.has(page.id)) continue
    repaired.add(page.id)
    if (page.parentId !== grammarPageId) await movePage(deps.db, { pageId: page.id,
      parentPageId: grammarPageId, userId: deps.userId, now: deps.now(), tel: deps.tel })
    const title = page.title === page.ruleKey
      ? cachedRule?.key === page.ruleKey && cachedRule.title ? cachedRule.title : ruleTitleFromKey(page.ruleKey)
      : page.title
    if (page.translatable || title !== page.title) await commitPageEdit(deps.db, {
      spaceId: deps.spaceId, pageId: page.id, fields: { title, translatable: false },
      userId: deps.userId, now: deps.now(), tel: deps.tel, frame,
    })
  }
  // A converted marker page or rules moved in here get their links; a settled Grammar costs no read.
  if (marker || repaired.size > 0) {
    const children = await deps.db.query`SELECT ${pageMetaColumns(deps.db.pages)} FROM ${deps.db.pages}
      WHERE ${pageParentColumn(deps.db.pages)} = ${grammarPageId} AND ${deps.db.pages.deletedAt.isNull()} ORDER BY ${deps.db.pages.title}, ${deps.db.pages.id}`
      .execute(pageMetaSchema(deps.spaceId))
    await _linkSubpages(deps, grammarPageId, children.map(page => page.id))
  }
  return grammarPageId
}

/** A parent lists its subpages as `page` blocks, as a user-inserted subpage does; links already present stay. */
async function _linkSubpages(deps: AdoptDeps, parentId: string, pageIds: string[]): Promise<void> {
  const row = await deps.db.query`SELECT ${pageReadColumns(deps.db.pages)} FROM ${deps.db.pages} WHERE ${deps.db.pages.id.eq(parentId)}`
    .executeOneOrThrow(pageReadSchema(deps.spaceId))
  const doc = crdt.load(row.data, { user: deps.userId })
  try {
    const current = pageDocument(doc).content.children as unknown as Blocks
    const linked = new Set<string>()
    const visit = (blocks: Blocks) => { for (const block of blocks) {
      if (block.type === 'page') linked.add(block.pageId)
      if ('children' in block && Array.isArray(block.children)) visit(block.children as Blocks)
    } }
    visit(current)
    const missing = pageIds.filter(id => !linked.has(id))
    if (missing.length === 0) return
    const version = doc.version()
    doc.edit(tx => { writeDocument(tx, [...structuredClone(current), ...missing.map((pageId): Blocks[number] => ({ id: blockId(), type: 'page', pageId }))], current) })
    const frame = doc.encodeUpdate(version).bytes
    await writeWorkspace({ writes: deps.writes, afterCommit: workspaceId => deps.afterCommit?.(workspaceId), afterLocalCommit: deps.afterLocalCommit }, deps.spaceId, () => commitPageEdit(deps.db, {
      spaceId: deps.spaceId, pageId: parentId, userId: deps.userId, now: deps.now(), tel: deps.tel, frame }))
  } finally { doc.free() }
}

async function _ensureRulePage(
  deps: AdoptDeps,
  languagePageId: string,
  grammarPageId: string,
  rule: CachedRule,
): Promise<string> {
  const pages = await deps.db.query`
    SELECT ${pageMetaColumns(deps.db.pages)}
    FROM ${deps.db.pages}
    WHERE ${pageParentColumn(deps.db.pages)} IN (SELECT value FROM json_each(${JSON.stringify([grammarPageId, languagePageId])}))
      AND ${deps.db.pages.id} IN (SELECT id FROM pages WHERE ${deps.db.pages.kind.eq('note')})
      AND ${deps.db.pages.deletedAt.isNull()}
      AND EXISTS(SELECT 1 FROM crdt_attr_lookup('idx_pages_crdt_attr',NULL,${deps.db.pages}.rowid) parts WHERE json_type(parts.fields,'$.qid')='text' AND json_extract(parts.fields,'$.type')='rule' AND json_extract(parts.fields,'$.key')=${rule.key})
  `.execute(pageMetaSchema(deps.spaceId))
  const existing = pages[0]
  const pageId = existing?.id ?? ulid()
  // D4: commit projects the class card from the engine-owned rule id.
  // Practice content remains derived from members for each repetition.
  if (!existing) {
    await _createPage(deps, {
      pageId,
      title: rule.title ?? ruleTitleFromKey(rule.key),
      readonly: true,
      parentId: grammarPageId,
      content: { kind: 'note', children: [rule.block] },
    })
    await _linkSubpages(deps, grammarPageId, [pageId])
  }
  return pageId
}

async function _createPage(
  deps: AdoptDeps,
  args: {
    pageId: string
    title: string
    translatable?: true
    /** Generated reference pages open in read mode (flashcards-editor-home.decisions.md "Read and edit modes"). */
    readonly?: true
    parentId: string | null
    content: PageContent
    questionBlocks?: Blocks
  },
): Promise<void> {
  const content: PageDocumentContent = args.content.kind === 'lexiconEntry'
    ? { ...args.content, children: args.questionBlocks ?? [] }
    : args.content
  const doc = newPageDocument({ ...(args.translatable ? { translatable: true } : {}), title: args.title,
    content, deletedAt: null }, deps.userId)
  await writeWorkspace({ writes: deps.writes, afterCommit: workspaceId => deps.afterCommit?.(workspaceId), afterLocalCommit: deps.afterLocalCommit }, deps.spaceId, () => commitPageEdit(deps.db, { fields: { title: args.title, content, translatable: args.translatable, readonly: args.readonly, deletedAt: null }, spaceId: deps.spaceId, pageId: args.pageId, parentId: args.parentId, userId: deps.userId, now: deps.now(), tel: deps.tel, frame: doc.encodeSnapshot().bytes }))
}

function _languageTitle(language: Language): string {
  return { ru: 'Русский', de: 'Deutsch', en: 'English' }[language]
}
